import { useState, useEffect, useRef, useCallback } from "react";
import { Message, ApiError } from "../types";
import { apiService } from "../services/api";
import { validateInput, chatRateLimiter } from "../utils/security";

export interface UseChatConfig {
  userMetadata?: {
    name?: string;
    email?: string;
    role?: string;
    department?: string;
    [key: string]: any;
  };
  apiUrl?: string;
  customHeaders?: Record<string, string>;
  getAuthToken?: () => string | Promise<string>;
  onTokenExpired?: () => Promise<void>;
  onMessage?: (message: string) => void;
  onResponse?: (response: string) => void;
  onError?: (error: any) => void;
}

export const useChat = (config: UseChatConfig = {}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [conversationId] = useState(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `conv_${timestamp}_${random}`;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isRetryingRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Check API connection on mount with adaptive interval
  useEffect(() => {
    const checkConnection = async () => {
      const healthy = await apiService.healthCheck();
      setIsConnected(healthy);
    };

    checkConnection();

    // Adaptive health check: check more frequently when disconnected
    const interval = setInterval(
      checkConnection,
      isConnected ? 30 * 1000 : 10 * 1000 // 30s when connected, 10s when disconnected
    );
    return () => clearInterval(interval);
  }, [isConnected]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      // Validate input
      const validation = validateInput.chatMessage(content);
      if (!validation.isValid) {
        const errorMessage: Message = {
          id: `msg_${Date.now()}_validation_error`,
          content: validation.error || "Invalid message",
          sender: "assistant",
          timestamp: new Date(),
          error: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
        return;
      }

      // Check rate limiting
      if (!chatRateLimiter.isAllowed()) {
        const timeUntilReset = Math.ceil(
          chatRateLimiter.getTimeUntilReset() / 1000
        );
        const rateLimitMessage: Message = {
          id: `msg_${Date.now()}_rate_limit`,
          content: `Rate limit exceeded. Please wait ${timeUntilReset} seconds before sending another message.`,
          sender: "assistant",
          timestamp: new Date(),
          error: true,
        };
        setMessages((prev) => [...prev, rateLimitMessage]);
        return;
      }

      // Only add user message if not a retry (avoid duplicate messages)
      if (!isRetryingRef.current) {
        const userMessage: Message = {
          id: `msg_${Date.now()}_user`,
          content: content.trim(),
          sender: "user",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        // Trigger onMessage callback
        config.onMessage?.(content.trim());
      }

      setIsLoading(true);

      try {
        // Prepare API call with user context
        const messagePayload = {
          message: content.trim(),
          conversationId,
          userMetadata: config.userMetadata,
        };

        // Get auth token from callback if provided
        const authToken = config.getAuthToken
          ? await config.getAuthToken()
          : undefined;

        const headers: Record<string, string> = {};
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const response = await apiService.sendMessage(
          messagePayload,
          config.apiUrl,
          headers
        );
        // Use 'answer' field first, fall back to 'response' for backward compatibility
        const responseText =
          response.answer || response.response || "No response received";

        const assistantMessage: Message = {
          id: `msg_${Date.now()}_assistant`,
          content: responseText,
          sender: "assistant",
          timestamp: new Date(),
          sources: response.sources,
          metadata: {
            intent: response.metadata?.intent,
            confidence: response.confidence,
            query_type: response.query_type,
            processing_time: response.processing_time,
            sql_query: response.metadata?.sql_query,
            row_count: response.metadata?.row_count,
            execution_time: response.metadata?.execution_time,
            database_used: response.metadata?.database_used,
          },
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setIsConnected(true);

        // Trigger onResponse callback
        config.onResponse?.(responseText);
      } catch (error) {
        const apiError = error as ApiError;

        // Handle token expiration (401/403) - attempt refresh and retry (only once to prevent loops)
        if (
          (apiError.status === 401 || apiError.status === 403) &&
          config.onTokenExpired &&
          !isRetryingRef.current
        ) {
          try {
            await config.onTokenExpired();
            // Mark as retrying to prevent duplicate user message
            isRetryingRef.current = true;
            // Token refreshed, retry the message automatically (via new sendMessage call)
            setIsLoading(false);
            sendMessage(content);
            return;
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            // Fall through to show error if refresh fails
          } finally {
            // Reset retry flag
            isRetryingRef.current = false;
          }
        }

        // Only mark as disconnected for actual connection errors, not auth errors
        if (apiError.status !== 401 && apiError.status !== 403) {
          setIsConnected(false);
        }

        let errorContent = `I encountered an error: ${apiError.detail}`;

        // Provide more specific error messages for different scenarios
        if (apiError.status === 401 || apiError.status === 403) {
          errorContent =
            "Authentication expired. Please log in again and try your message.";
        } else if (apiError.status === 408) {
          errorContent =
            "The query took too long to process. Please try simplifying your request or contact support if this persists.";
        } else if (apiError.status === 500) {
          errorContent =
            "There was a server error processing your request. Please try again in a moment.";
        } else if (apiError.status === 429) {
          errorContent =
            "Too many requests. Please wait a moment before trying again.";
        } else if (apiError.detail.toLowerCase().includes("sql")) {
          errorContent =
            "There was an issue with the database query. Please rephrase your question or try a different approach.";
        }

        const errorMessage: Message = {
          id: `msg_${Date.now()}_error`,
          content: errorContent,
          sender: "assistant",
          timestamp: new Date(),
          error: true,
        };

        setMessages((prev) => [...prev, errorMessage]);

        // Trigger onError callback
        config.onError?.(apiError);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, isLoading, config]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Handle suggested queries from welcome screen
  useEffect(() => {
    const handleSuggestedQuery = (event: CustomEvent<string>) => {
      sendMessage(event.detail);
    };

    window.addEventListener(
      "sendSuggestedQuery",
      handleSuggestedQuery as EventListener
    );
    return () => {
      window.removeEventListener(
        "sendSuggestedQuery",
        handleSuggestedQuery as EventListener
      );
    };
  }, [sendMessage]);

  return {
    messages,
    isLoading,
    isConnected,
    conversationId,
    messagesEndRef,
    sendMessage,
    clearMessages,
  };
};
